import { useContext, useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { PlaycastContext } from '../../PlayerContext';
import { store } from '../store';
import { faGamepadModern, faKeyboard, faMouse } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// REMOVED: InputCapture import since we're not using it
// import { captureStore, InputCapture, useAnimationFrame } from '@playcastdotio/InputCapture';
import { captureStore } from '@playcastdotio/InputCapture';
import { isSafari, useIsMobileDevice } from '../../../utility/utility';
import { resetFirstInputDetection } from '../../../api/modules/hostMessaging/inputEvents';
import { resetGamepadSelectionForNewSession } from '../../../utility/gamepadSelection';
import { NotificationMessageType } from '@playcastdotio/Messaging';
import { sendMessage } from '../../../api/modules/realtimeMessaging';
import { websocket } from '@playcastdotio/Messaging';
import { getPersistentPlayerId } from '../../../api';
import { sendAnalytics, PlaycastAnalyticsEmailSubmitted, PlaycastAnalyticsEmailSent, PlaycastAnalyticsUserExited } from '@playcastdotio/Analytics';

// Mobile Overlay Component
const MobileOverlay = ({ data, access, startPlayerMode, handleBrainrotClick, isTransitioning }: {
  data: unknown;
  access: { gamepad?: boolean; keyboard?: boolean; mouse?: boolean; touch?: boolean };
  startPlayerMode: () => void;
  handleBrainrotClick: () => void;
  isTransitioning?: boolean;
}) => {
  return (
    <div className='mx-auto flex w-full max-w-md flex-col items-center gap-6 rounded-md bg-slate-800 bg-opacity-95 p-8'>
      <div>
        <img src='/images/playcast_logo-96x96.png' alt='Logo' className='size-24' />
      </div>
      <div className='space-y-2'>
        <div className='text-center text-yellow-400'>Your controls:</div>
        <div className='flex flex-row gap-4 text-yellow-400'>
          <FontAwesomeIcon icon={faGamepadModern} size='xl' className={(access.gamepad ?? false) ? '' : 'opacity-30'} />
          <FontAwesomeIcon icon={faKeyboard} size='xl' className={(access.keyboard ?? false) ? '' : 'opacity-30'} />
          <FontAwesomeIcon icon={faMouse} size='xl' className={(access.mouse ?? false) ? '' : 'opacity-30'} />
        </div>
      </div>
      <div className='flex gap-4'>
        <div className="flex flex-col items-center gap-2">
          <button
            className='xs:block rounded-full border-2 border-gray-200 bg-amber-400 px-4 py-1.5 text-sm font-bold text-gray-900 shadow-sm hover:bg-amber-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 disabled:opacity-50 disabled:cursor-not-allowed'
            onClick={startPlayerMode}
            disabled={isTransitioning}
            data-testid="play-now-button"
          >
            {isTransitioning ? 'Starting...' : 'Play Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Desktop Overlay Component
const DesktopOverlay = ({ data, access, startPlayerMode, handleBrainrotClick, shouldShowOverlay, isTransitioning }: {
  data: unknown;
  access: { gamepad?: boolean; keyboard?: boolean; mouse?: boolean; touch?: boolean };
  startPlayerMode: () => void;
  handleBrainrotClick: () => void;
  shouldShowOverlay: boolean;
  isTransitioning?: boolean;
}) => {
  // Check if host has granted any input access
  const hasInputAccess = access.gamepad || access.keyboard || access.mouse || access.touch;

  return (
    <div className='mx-auto flex w-full max-w-md flex-col items-center gap-6 rounded-md bg-slate-800 bg-opacity-95 p-8'>
      <div>
        <img src='/images/playcast_logo-96x96.png' alt='Logo' className='size-24' />
      </div>
      <div className='space-y-2'>
        <div className='text-center text-yellow-400'>Your controls:</div>
        <div className='flex flex-row gap-4 text-yellow-400'>
          <FontAwesomeIcon icon={faGamepadModern} size='xl' className={(access.gamepad ?? false) ? '' : 'opacity-30'} />
          <FontAwesomeIcon icon={faKeyboard} size='xl' className={(access.keyboard ?? false) ? '' : 'opacity-30'} />
          <FontAwesomeIcon icon={faMouse} size='xl' className={(access.mouse ?? false) ? '' : 'opacity-30'} />
        </div>
      </div>
      <div className='flex gap-4'>
        <div className="flex flex-col items-center gap-2">
          {/* Play Now button - simplified */}
          <div className="flex items-center gap-2">
            {!hasInputAccess ? (
              <button
                type="button"
                className="xs:block rounded-full border-2 border-gray-200 bg-amber-400 px-4 py-1.5 text-sm font-bold text-gray-900 shadow-sm transition-all duration-200 hover:bg-amber-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600 disabled:opacity-60 disabled:cursor-not-allowed"
                disabled
              >
                Play Now
              </button>
            ) : (
              <button
                type="button"
                className={`xs:block rounded-full border-2 border-gray-200 bg-amber-400 px-4 py-1.5 text-sm font-bold text-gray-900 shadow-sm transition-all duration-200 hover:bg-amber-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600`}
                onClick={startPlayerMode}
                disabled={isTransitioning}
                data-testid="play-now-button"
              >
                {isTransitioning ? 'Starting...' : 'Play Now'}
              </button>
            )}
          </div>

          {window.location.pathname.includes('/j/brainrot') && (
            <button
              onClick={handleBrainrotClick}
              onTouchStart={handleBrainrotClick}
              className="hover:scale-110 active:scale-95 transition-transform cursor-pointer p-0 m-0 border-none bg-transparent"
              aria-label="Brainrot"
              style={{ lineHeight: 0 }}
            >
              <img
                src="/desktopBrainrotButton.svg"
                alt="Brainrot"
                className="w-32 h-16 block m-0 p-0"
                draggable={false}
                style={{ display: 'block', margin: 0, padding: 0 }}
              />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const Options = () => {
  const data = useContext(PlaycastContext);
  // Use real access permissions from captureStore
  const access = useSyncExternalStore(captureStore.subscribeAccess, captureStore.getAccess);
  // Reactive mobile detection - updates when viewport changes (e.g., "Request Desktop Site")
  const isMobileDevice = useIsMobileDevice();
  const [pointerLockRequested, setPointerLockRequested] = useState(false);
  const [fullscreenEnteredTime, setFullscreenEnteredTime] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleBrainrotClick = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  // Email validation regex (copied from SendInvitePanel)
  const isValidEmail = (email: string): boolean => {
    const regexPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regexPattern.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailSent(false);
  };

  const handleSend = () => {
    if (!isValidEmail(email)) return;
    // Send NOTIFICATION_FORTNITE_REMINDER notification

    sendAnalytics({
      name: 'Email Submitted',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      email: email,
    } as PlaycastAnalyticsEmailSubmitted);

    const outboundMessage = websocket.generators.notification(NotificationMessageType.NOTIFICATION_FORTNITE_REMINDER, { email });
    sendMessage(outboundMessage);
    setEmailSent(true);
    setEmail('');

    sendAnalytics({
      name: 'Email Sent',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      email: email,
    } as PlaycastAnalyticsEmailSent);
  };
  // Mobile-specific player mode activation
  const startPlayerModeForMobile = async () => {

    // Already in standalone mode (PWA - already fullscreen). Safari is included:
    // the join click requests fullscreen immediately instead of skipping it.
    // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
    if (window.navigator.standalone) {
      console.log('Skipping fullscreen - already in standalone/PWA mode');
    } else {
      try {
        // Try modern API first, with fallbacks for older Android browsers
        const element = document.body;
        if (element.requestFullscreen) {
          // Safari rejects navigationUI and would otherwise stay on "Starting...".
          await element.requestFullscreen(isSafari() ? undefined : { navigationUI: 'hide' });
        } else if ('webkitRequestFullscreen' in element) {
          // @ts-expect-error: webkitRequestFullscreen is a legacy API
          await element.webkitRequestFullscreen();
        } else if ('mozRequestFullScreen' in element) {
          // @ts-expect-error: mozRequestFullScreen is a legacy API
          await element.mozRequestFullScreen();
        }
        // Track when fullscreen was entered
        setFullscreenEnteredTime(Date.now());
      } catch (e) {
        console.log('Mobile fullscreen not supported on this device:', e);
      }
    }

    // Directly activate player mode - this is the key difference
    store.updatePlayerMode('player');
  };

  // Desktop-specific player mode activation
  const startPlayerModeForDesktop = async () => {
    console.log('Starting desktop player mode...');

    // Safari must enter fullscreen on the click, before pointer lock. Its
    // pointer-lock permission query does not settle and was holding "Starting...".
    if (isSafari()) {
      try {
        await document.documentElement.requestFullscreen();
        setFullscreenEnteredTime(Date.now());
      } catch (e) {
        console.log('Desktop fullscreen not supported');
      }
      store.updatePlayerMode('player');
      return;
    }

    // Desktop browser detection
    let oldRequest = isSafari();
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

    if (!isSafari()) {
      try {
        if (isFirefox) {
          // on Firefox, we need to request fullscreen first
          // in order to request pointer lock and keyboard lock
          try {
            await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
            // Track when fullscreen was entered
            setFullscreenEnteredTime(Date.now());
          } catch (e) {
            console.log('Fullscreen not supported');
          }
        }

        const pointerLock = await navigator.permissions.query({ name: 'pointer-lock' as PermissionName });
        const keyboardLock = await navigator.permissions.query({ name: 'keyboard-lock' as PermissionName });

        if (pointerLock.state === 'granted' || pointerLock.state === 'prompt') {
          try {
            if (!document.pointerLockElement) {
              document.body.requestPointerLock({ unadjustedMovement: true });
            }
          } catch (e: unknown) {
            if (e instanceof Error && e.name === 'InUseAttributeError') {
              console.log('Pointer lock already in progress, waiting...');
            } else {
              console.log('Pointer lock not supported:', e);
            }
          }
        }

        if (keyboardLock.state === 'granted' || keyboardLock.state === 'prompt') {
          try {
            // try to lock the keyboard
            await navigator.keyboard.lock();
          } catch (e) {
            console.log('Keyboard lock not supported');
          }
        }
      } catch (e) {
        console.log('Pointer or keyboard lock permission API not supported, trying to request normally');
        oldRequest = true;
      }
    }

    if (oldRequest) {
      // Just request pointer lock and keyboard lock as normal
      try {
        if (!document.pointerLockElement) {
          document.body.requestPointerLock({ unadjustedMovement: true });
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.name === 'InUseAttributeError') {
          console.log('Pointer lock already in progress, waiting...');
        } else {
          console.log('Pointer lock not supported:', e);
        }
      }
      try {
        // try to lock the keyboard
        await navigator.keyboard.lock();
      } catch (e) {
        console.log('Keyboard lock not supported');
      }
    }

    // Desktop fullscreen handling
    // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
    if (!window.navigator.standalone) {
      try {
        await document.body.requestFullscreen({ navigationUI: 'hide' });
        // Track when fullscreen was entered
        setFullscreenEnteredTime(Date.now());
      } catch (e) {
        console.log('Desktop fullscreen not supported');
      }
    }

    store.updatePlayerMode('player');
    console.log('Desktop player mode activated successfully');
  };

  // Main startPlayerMode function - routes to mobile or desktop
  const startPlayerMode = async () => {
    // Only prevent if pointer lock is actually active, not just requested
    if (document.pointerLockElement) {
      console.log('Pointer lock already active');
      return;
    }

    // Only prevent if we're in the middle of a request
    if (pointerLockRequested) {
      console.log('Pointer lock request in progress');
      return;
    }

    setPointerLockRequested(true);
    setIsTransitioning(true);

    try {
      if (isMobileDevice) {
        await startPlayerModeForMobile();
      } else {
        await startPlayerModeForDesktop();
      }
    } finally {
      // Reset the flags
      setTimeout(() => {
        setPointerLockRequested(false);
        setIsTransitioning(false);
      }, 100);
    }
  };

  const endPlayerMode = async () => {
    // @ts-expect-error: Property 'standalone' does not exist on type 'Navigator'
    if (!document.fullscreenElement && !window.navigator.standalone) {
      // For mobile devices that don't support fullscreen (iPhone/Safari), always end player mode
      // For all other devices, only end if we're not in fullscreen
      if (isSafari() || !isMobileDevice) {
        store.updatePlayerMode('spectator');
        // Clear the fullscreen entered timestamp when exiting
        setFullscreenEnteredTime(null);
      }
    }
  };

  useEffect(() => {
    // Add fullscreen change listener for desktop and mobile devices that support fullscreen
    // Skip only for iPhone/Safari which doesn't support fullscreen
    if (isSafari()) return;

    document.addEventListener('fullscreenchange', endPlayerMode, false);

    return () => {
      document.removeEventListener('fullscreenchange', endPlayerMode, false);
    };
  }, []);

  useEffect(() => {
    // Only show options if we have any type of input access
    if (access.gamepad || access.keyboard || access.mouse || access.touch) {
      store.updatePlayerOptionsOpen(true);
    }

    // Only exit fullscreen and update to spectator if we have NO input access at all
    const hasNoAccess = !(access.gamepad ?? false) && !(access.keyboard ?? false) && !(access.mouse ?? false) && !(access.touch ?? false);
    if (hasNoAccess) {
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen();
      }
      store.updatePlayerMode('spectator');
    }
  }, [access.gamepad, access.keyboard, access.mouse, access.touch]);

  const handleMouseMove = useCallback(() => {
    // Allow a grace period of 1500ms after entering fullscreen
    // before exiting if pointer lock isn't established
    const GRACE_PERIOD_MS = 1500;

    if (!document.pointerLockElement && document.fullscreenElement && document.exitFullscreen) {
      // Only exit if we're past the grace period
      if (!fullscreenEnteredTime || Date.now() - fullscreenEnteredTime > GRACE_PERIOD_MS) {
        document.exitFullscreen();
      }
    }
  }, [fullscreenEnteredTime]);

  useEffect(() => {
    // Should not handle mouse move on iPhone/Safari as it causes weird behavior
    // Other mobile devices that support fullscreen should be fine
    if (isSafari()) return;
    // Also skip for mobile devices that don't have mouse input
    if (isMobileDevice && !window.matchMedia('(any-hover: hover)').matches) return;

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseMove]);

  // Update fullscreenEnteredTime when fullscreen state changes
  useEffect(() => {
    // Add fullscreen change listener for desktop and mobile devices that support fullscreen
    // Skip only for iPhone/Safari which doesn't support fullscreen
    if (isSafari()) return;

    const handleFullscreenChange = () => {
      if (document.fullscreenElement) {
        setFullscreenEnteredTime(Date.now());
      } else {
        setFullscreenEnteredTime(null);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Watch for the play now modal becoming visible and reset gamepad selection
  useEffect(() => {
    // Check if the overlay/modal is currently visible
    const isOverlayVisible = data.mode.mode === 'spectator' && data.options.open;

    // When the overlay becomes visible, reset gamepad detection
    if (isOverlayVisible) {
      resetFirstInputDetection();
      resetGamepadSelectionForNewSession();
    }
  }, [data.mode.mode, data.options.open]);



  // Determine which overlay to render based on device type
  const shouldShowOverlay = data.mode.mode === 'spectator' &&
    data.options.open &&
    ((access.gamepad ?? false) || (access.keyboard ?? false) || (access.mouse ?? false) || (access.touch ?? false));

  // Reset first-input detection when play now overlay becomes visible
  // This allows players to change controllers mid-session
  useEffect(() => {
    if (shouldShowOverlay) {
      // Reset both the first-input detection and localStorage selection
      resetFirstInputDetection();
      resetGamepadSelectionForNewSession();
    }
  }, [shouldShowOverlay]);

  // REMOVED: captureStore access update since we're not using InputCapture
  // useEffect(() => {
  //   if (shouldShowOverlay && !isMobileDevice()) {
  //     // Enable gamepad detection for the overlay
  //     captureStore.updateAccess({
  //       mouse: false,
  //       keyboard: false,
  //       touch: false,
  //       gamepad: true,
  //     });
  //   }
  // }, [shouldShowOverlay]);

  return (
    <>
      {/* REMOVED: InputCapture component to prevent conflicts with Player.tsx */}
      {/* {shouldShowOverlay && !isMobileDevice() && (
        <InputCapture skipGamepad={false} />
      )} */}

      {shouldShowOverlay && (
        isMobileDevice ? (
          <MobileOverlay
            key="mobile-overlay"
            data={data}
            access={access}
            startPlayerMode={startPlayerMode}
            handleBrainrotClick={handleBrainrotClick}
            isTransitioning={isTransitioning}
          />
        ) : (
          <DesktopOverlay
            key={`desktop-overlay-${window.location.pathname}`}
            data={data}
            access={access}
            startPlayerMode={startPlayerMode}
            handleBrainrotClick={handleBrainrotClick}
            shouldShowOverlay={shouldShowOverlay}
            isTransitioning={isTransitioning}
          />
        )
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative rounded-lg shadow-lg p-0 max-w-md w-full mx-4 flex flex-col items-center max-h-screen sm:max-h-[90vh] overflow-y-auto" style={{ background: '#26487E' }}>
            <button
              type="button"
              onClick={handleCloseModal}
              className="absolute top-2 right-2 text-2xl font-bold text-white hover:text-gray-200 focus:outline-none"
              aria-label="Close"
            >
              ×
            </button>
            {/* Logos Row */}
            <div className="flex flex-row items-center justify-center gap-2 pt-8 pb-4 w-full">
              <img src="/GradientLogo.svg" alt="Logo" className="h-8 w-auto" />
              <img src="/PlaycastPavilionText.svg" alt="Playcast Pavilion" className="h-8 w-auto" />
            </div>
            {/* Promo Image */}
            <a
              href="https://www.fortnite.com/play/island/1514-8773-6943?utm_source=playcast&utm_medium=interactive&utm_campaign=brainrot-july-2025"
              target="_blank"
              rel="noopener noreferrer"
              className="block flex justify-center"
              onClick={() => {
                sendAnalytics({
                  name: 'User Exited',
                  role: 'host',
                  persistentId: getPersistentPlayerId(),
                  destination:
                    'https://www.fortnite.com/play/island/1514-8773-6943?utm_source=playcast&utm_medium=interactive&utm_campaign=brainrot-july-2025',
                } as PlaycastAnalyticsUserExited);
              }}
            >
              <img src="/fortnitePromo.webp" alt="Fortnite Promo" className="w-3/4 max-w-xs rounded mb-4 cursor-pointer hover:opacity-90 transition-opacity" />
            </a>
            {/* OR */}
            <div className="text-white text-lg font-bold mb-2">OR</div>
            {/* Receive a link */}
            <div className="text-white text-2xl font-bold mb-1">Receive a link</div>
            {/* Description */}
            <div className="text-white text-center text-base font-normal mb-2 px-4">We'll send you a link to try MY SINGING BRAINROT 🎤 (NEW) whenever you want</div>
            {/* Email input and send button */}
            <div className="w-full px-4 mb-6">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your email"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded text-black"
                  disabled={emailSent}
                />
                <button
                  onClick={handleSend}
                  disabled={!isValidEmail(email) || emailSent}
                  className="px-4 py-2 bg-amber-400 text-black font-bold rounded hover:bg-amber-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailSent ? 'Sent!' : 'Send Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
